import type { FinancialAssumptions } from '../../types/financialAssumptions';
import { resolveFinancialAssumptions } from '../../types/financialAssumptions';
import { createRegistryResolver, tierSource } from './resolveCore';
import type { FieldInputResolution, RegistryResolver, ResolverFactoryOptions } from './types';

export interface FinancialAssumptionsResolverInput extends ResolverFactoryOptions {
  siteAssumptions?: FinancialAssumptions;
  reportOverride?: FinancialAssumptions;
  /** Per-field tier-3 LLM-extracted fallbacks keyed by FA field path. */
  llmFallbacks?: Record<string, unknown>;
}

/*
 * 6.1 — `opexPerKwYearUsd` and `discountRatePct` WERE HERE AND ARE NOT FIELDS.
 *
 * Neither exists on `FinancialAssumptionsCore`, so `opts.siteAssumptions?.[key]`
 * and `opts.reportOverride?.[key]` were `undefined` on every site ever built:
 * the resolver walked two keys that could not resolve, and any consumer asking
 * for them got a permanent "not established". The same dead key sank the
 * `discountRatePct` fact descriptor, retired under 3.8 for the same reason.
 *
 * Their real counterparts are `fixedOmPerKwYearUsd` and `waccPct`. They are
 * NOT substituted in here: adding a field to this list makes it resolvable as a
 * tier-1 source in prompts and the registry, which is a deliberate change to
 * what the model is told is authoritative — a decision, not a repair.
 */
const FA_FIELD_KEYS = [
  'capacityFactorPct',
  'capexPerKwUsd',
  'ppaPricePerMwhUsd',
] as const;

export function createFinancialAssumptionsResolver(
  opts: FinancialAssumptionsResolverInput,
): RegistryResolver {
  const resolved = resolveFinancialAssumptions(opts.siteAssumptions, opts.reportOverride);
  const fieldSources: Record<string, ReturnType<typeof tierSource>[]> = {};

  for (const key of FA_FIELD_KEYS) {
    const sources = [];
    const siteVal = opts.siteAssumptions?.[key as keyof FinancialAssumptions];
    const overrideVal = opts.reportOverride?.[key as keyof FinancialAssumptions];
    const llmVal = opts.llmFallbacks?.[key];

    if (overrideVal !== undefined && overrideVal !== null) {
      sources.push(
        tierSource(1, overrideVal, {
          kind: 'report-override',
          ref: `report:financialAssumptionsOverride.${key}`,
          label: 'Report override',
        }),
      );
    }
    if (siteVal !== undefined && siteVal !== null) {
      sources.push(
        tierSource(1, siteVal, {
          kind: 'site-canonical',
          ref: `site:financialAssumptions.${key}`,
          label: 'Site canonical',
        }),
      );
    }
    if (llmVal !== undefined && llmVal !== null) {
      sources.push(
        tierSource(3, llmVal, {
          kind: 'llm-inference',
          ref: `llm:financialAssumptions.${key}`,
          label: 'LLM extraction',
        }),
      );
    }
    if (sources.length > 0) fieldSources[key] = sources;
  }

  if (resolved && Object.keys(fieldSources).length === 0) {
    fieldSources['__aggregate'] = [
      tierSource(1, resolved, {
        kind: 'site-canonical',
        ref: 'site:financialAssumptions',
        label: 'Resolved FA bundle',
      }),
    ];
  }

  return createRegistryResolver({
    registryId: 'financialAssumptions',
    siteId: opts.siteId,
    fieldSources,
    now: opts.now,
  });
}

export function resolveFinancialAssumptionField(
  resolver: RegistryResolver,
  fieldKey: string,
): FieldInputResolution | null {
  return resolver.resolveField(fieldKey);
}
